import {PolicyFilter, DocumentsFilter} from 'modules/decisionsNext/api/types';
import {SettingsFilter} from 'modules/settings/features/filters/types';

export type Filters = {
  documents: DocumentsFilter;
  policy: PolicyFilter;
  settings: SettingsFilter;
};

export type PageFilter = {
  [K in keyof Filters]: Filters[K] & {
    _type: K;
  };
}[keyof Filters];

const createFilterTypeGuard = <K extends keyof Filters>(type: K) => {
  return (filter: PageFilter | undefined): filter is Extract<PageFilter, {_type: K}> => {
    return filter?._type === type;
  };
};

const createFilterAssert = <K extends keyof Filters>(type: K) => {
  return (filter: PageFilter) => {
    if (filter._type !== type) {
      throw new Error(`Expected filter type "${type}", got "${filter._type}"`, {
        cause: filter,
      });
    }
  };
};

export function assertPageFilter(filter: PageFilter | undefined): asserts filter is PageFilter {
  if (!filter) {
    throw new Error('Filter is undefined');
  }
}

// Assertion requires explicit type annotation to infer the correct type
type AssertOf<T extends keyof Filters> = (filter: PageFilter) => asserts filter is Extract<PageFilter, {_type: T}>;

export type PolicyPageFilter = Extract<PageFilter, {_type: 'policy'}>;
export const isPolicyFilter = createFilterTypeGuard('policy');
export const assertPolicyFilter: AssertOf<'policy'> = createFilterAssert('policy');

export type DocumentsPageFilter = Extract<PageFilter, {_type: 'documents'}>;
export const isDocumentsFilter = createFilterTypeGuard('documents');
export const assertDocumentsFilter: AssertOf<'documents'> = createFilterAssert('documents');

export type SettingsPageFilter = Extract<PageFilter, {_type: 'settings'}>;
export const isSettingsFilter = createFilterTypeGuard('settings');
export const assertSettingsFilter: AssertOf<'settings'> = createFilterAssert('settings');
